import { normalizeRotationDegrees } from './media-rotation.util';

// Shared pure helpers for rendering a zoomed-in preview of a face rectangle (normalized 0..1
// coordinates) against an image's natural pixel dimensions. Used by both the pending-faces
// review list in media-lightbox.ts (automatic face detection) and the "who is this" person
// picker's split-view panel (manual marker drawing), so both stay visually consistent.

export interface FaceRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Below this on-screen face size, cropping tightly to the face itself (with no margin) already
// fills the preview box adequately — adding the usual padding would just zoom out unnecessarily
// on already-tiny faces. Above the threshold there's room to add breathing space around the face.
export function shouldTightlyZoomFace(
  face: FaceRect,
  naturalWidth: number,
  naturalHeight: number,
  zoomThresholdPx: number,
): boolean {
  if (naturalWidth <= 0 || naturalHeight <= 0) return true;
  return face.width * naturalWidth < zoomThresholdPx || face.height * naturalHeight < zoomThresholdPx;
}

export function getFaceCropRect(
  face: FaceRect,
  naturalWidth: number,
  naturalHeight: number,
  zoomThresholdPx: number,
): FaceRect {
  if (shouldTightlyZoomFace(face, naturalWidth, naturalHeight, zoomThresholdPx)) return face;
  const padding = 0.18;
  const x = Math.max(0, face.x - face.width * padding);
  const y = Math.max(0, face.y - face.height * padding);
  const right = Math.min(1, face.x + face.width * (1 + padding));
  const bottom = Math.min(1, face.y + face.height * (1 + padding));
  return {
    x,
    y,
    width: Math.max(0.0001, right - x),
    height: Math.max(0.0001, bottom - y),
  };
}

export function faceCropStyle(cropRect: FaceRect): string {
  return `width: ${100 / cropRect.width}%; height: ${100 / cropRect.height}%; left: -${(cropRect.x / cropRect.width) * 100}%; top: -${(cropRect.y / cropRect.height) * 100}%; position: absolute; max-width: none; max-height: none;`;
}

// The preview box itself can't just be a fixed square — that would force faceCropStyle()'s <img>
// (sized/positioned purely as percentages of this box) to stretch non-uniformly whenever the crop
// rect's real pixel aspect ratio isn't 1:1. Sizing the box to match that real aspect ratio (capped
// to maxSizePx on the longer side) keeps the percentage math in faceCropStyle() distortion-free.
export function facePreviewContainerStyle(
  cropRect: FaceRect,
  naturalWidth: number,
  naturalHeight: number,
  maxSizePx: number,
  rotationDegrees = 0,
): string {
  const size = getFacePreviewSize(cropRect, naturalWidth, naturalHeight, maxSizePx);
  const rotation = normalizeRotationDegrees(rotationDegrees);
  const quarterTurn = rotation === 90 || rotation === 270;
  const width = quarterTurn ? size.height : size.width;
  const height = quarterTurn ? size.width : size.height;
  return `width: ${width}px; height: ${height}px;`;
}

// The source crop is kept in its natural (unrotated) aspect ratio and rotated as one centered
// surface. The containing box is sized separately to the rotated aspect ratio so a 90/270 degree
// turn cannot expose a different part of the source crop through overflow clipping.
export function facePreviewSurfaceStyle(
  cropRect: FaceRect,
  naturalWidth: number,
  naturalHeight: number,
  maxSizePx: number,
  rotationDegrees = 0,
): string {
  const size = getFacePreviewSize(cropRect, naturalWidth, naturalHeight, maxSizePx);
  const rotation = normalizeRotationDegrees(rotationDegrees);
  return `position: absolute; left: 50%; top: 50%; width: ${size.width}px; height: ${size.height}px; transform: translate(-50%, -50%) rotate(${rotation}deg); transform-origin: center;`;
}

function getFacePreviewSize(
  cropRect: FaceRect,
  naturalWidth: number,
  naturalHeight: number,
  maxSizePx: number,
): { width: number; height: number } {
  let aspect = 1;
  if (naturalWidth > 0 && naturalHeight > 0 && cropRect.width > 0 && cropRect.height > 0) {
    const cropWidthPx = cropRect.width * naturalWidth;
    const cropHeightPx = cropRect.height * naturalHeight;
    if (cropWidthPx > 0 && cropHeightPx > 0) aspect = cropWidthPx / cropHeightPx;
  }
  const width = aspect >= 1 ? maxSizePx : Math.round(maxSizePx * aspect);
  const height = aspect >= 1 ? Math.round(maxSizePx / aspect) : maxSizePx;
  return { width, height };
}
